// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - PRICE API (SERVERLESS VERSION)
// Fetches real-time token prices via Vercel serverless function
// ═══════════════════════════════════════════════════════════════════════════

const PRICE_API_ENDPOINT = '/api/prices'; // Our Vercel serverless function!
const CACHE_DURATION = 300000; // 5 minutes in milliseconds

// ═══════════════════════════════════════════════════════════════════════════
// TOKEN ID MAPPING - CoinGecko IDs for major tokens (still used for reference)
// ═══════════════════════════════════════════════════════════════════════════

const TOKEN_ID_MAP = {
  // Stablecoins
  'USDC': 'usd-coin',
  'USDT': 'tether',
  'FDUSD': 'first-digital-usd',
  'DAI': 'dai',
  
  // Major Sui DeFi
  'SUI': 'sui',
  'CETUS': 'cetus-protocol',
  'DEEP': 'deepbook-protocol',
  'TURBOS': 'turbos-finance',
  'SCA': 'scallop',
  'SCALLOP': 'scallop',
  'NAVX': 'navi-protocol',
  'NAVI': 'navi-protocol',
  'BUCK': 'bucket-protocol',
  'ALPHAFI': 'alphafi',
  'ALPHA': 'alphafi',
  
  // Liquid Staking
  'AFSUI': 'afsui',
  'VSUI': 'volo-staked-sui',
  'HASUI': 'haedal-staked-sui',
  'SSUI': 'staked-sui',
  'STSUI': 'staked-sui',
  
  // Other major tokens
  'NS': 'namespace',
  'FUD': 'fud-the-pug',
  'BLUB': 'blub',
  'SUIA': 'suia',
  'TOILET': 'toilet-dust',  // 🚽 Partnership token!
};

// ═══════════════════════════════════════════════════════════════════════════
// PRICE CACHE
// ═══════════════════════════════════════════════════════════════════════════

const priceCache = {
  data: {}, // { symbol: price }
  lastUpdated: {}, // { symbol: timestamp }
};

// ═══════════════════════════════════════════════════════════════════════════
// CORE FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Extract token symbol from object type
 * @param {string} objectType - Full object type (e.g., "0x...::usdc::USDC")
 * @returns {string} Token symbol in uppercase
 */
export function extractTokenSymbol(objectType) {
  if (!objectType) return '';
  
  // Try to get last part after ::
  const parts = objectType.split('::');
  const lastPart = parts[parts.length - 1];
  
  // 🔥 FIX: Remove trailing > from Coin<TYPE> format
  const cleaned = lastPart.replace(/>/g, '').trim();
  
  // Clean up and return uppercase
  return cleaned.toUpperCase();
}

/**
 * Get CoinGecko ID for a token symbol
 * @param {string} symbol - Token symbol (e.g., "USDC")
 * @returns {string|null} CoinGecko ID or null
 */
function getCoinGeckoId(symbol) {
  if (!symbol) return null;
  
  const upperSymbol = symbol.toUpperCase();
  return TOKEN_ID_MAP[upperSymbol] || null;
}

/**
 * Fetch token price from our serverless function
 * @param {string} tokenType - Full token type string
 * @returns {Promise<number|null>} Price in USD or null
 */
export async function fetchTokenPrice(tokenType) {
  if (!tokenType) return null;
  
  const symbol = extractTokenSymbol(tokenType);
  
  if (!symbol) {
    console.log(`⚠️ No symbol extracted from: ${tokenType}`);
    return null;
  }
  
  // Check cache
  const cached = priceCache.data[symbol];
  const cacheTime = priceCache.lastUpdated[symbol];
  
  if (cached && cacheTime && (Date.now() - cacheTime < CACHE_DURATION)) {
    console.log(`💰 Using cached price for ${symbol}: $${cached}`);
    return cached;
  }
  
  // Fetch from our serverless function
  try {
    console.log(`📡 Fetching price for ${symbol} via serverless...`);
    
    const response = await fetch(`${PRICE_API_ENDPOINT}?symbols=${symbol}`);
    
    if (!response.ok) {
      console.log(`❌ Price fetch failed: HTTP ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    const price = data.prices?.[symbol] || null;
    
    if (price) {
      // Cache the price
      priceCache.data[symbol] = price;
      priceCache.lastUpdated[symbol] = Date.now();
      console.log(`✅ ${symbol} price: $${price}`);
      return price;
    }
    
    console.log(`⚠️ No price data for ${symbol}`);
    return null;
  } catch (error) {
    console.error(`❌ Error fetching price for ${symbol}:`, error.message);
    return null;
  }
}

/**
 * Calculate USD value from balance and price
 * @param {number|string} balance - Raw balance (in smallest unit) - can be string!
 * @param {number} decimals - Token decimals (usually 6 or 9)
 * @param {number} price - Price per token in USD
 * @returns {number|null} USD value or null
 */
export function calculateUSDValue(balance, decimals, price) {
  if (!balance || !price) return null;
  
  try {
    // 🔥 FIX: Convert string balance to number
    const balanceNum = typeof balance === 'string' ? Number(balance) : balance;
    
    if (isNaN(balanceNum)) {
      console.warn('Invalid balance value:', balance);
      return null;
    }
    
    const actualBalance = balanceNum / Math.pow(10, decimals || 9);
    const usdValue = actualBalance * price;
    
    console.log('💵 [CALC] Balance:', balanceNum, '→ Actual:', actualBalance, '→ USD:', usdValue);
    
    return usdValue;
  } catch (error) {
    console.error('Error calculating USD value:', error);
    return null;
  }
}

/**
 * Get value tier for color coding
 * @param {number} value - USD value
 * @returns {string} Tier: 'high', 'medium', 'low', 'dust', or 'unknown'
 */
export function getValueTier(value) {
  if (value === null || value === undefined) return 'unknown';
  
  if (value >= 100) return 'high';
  if (value >= 10) return 'medium';
  if (value >= 1) return 'low';
  if (value < 0.01) return 'dust';
  return 'low';
}

/**
 * Get value label text
 * @param {number} value - USD value
 * @returns {string|null} Label text or null
 */
export function getValueLabel(value) {
  if (value === null || value === undefined) return null;
  
  if (value >= 100) return '⚠️ HIGH VALUE';
  if (value < 0.01) return '🗑️ DUST';
  return null;
}

/**
 * Format USD value for display
 * @param {number} value - USD value
 * @returns {string} Formatted value
 */
export function formatUSDValue(value) {
  if (value === null || value === undefined) return 'Price unavailable';
  
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(2)}K`;
  }
  
  if (value >= 1) {
    return `$${value.toFixed(2)}`;
  }
  
  if (value >= 0.01) {
    return `$${value.toFixed(2)}`;
  }
  
  // Very small values
  return `$${value.toFixed(4)}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// BATCH OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fetch prices for multiple tokens at once via serverless function
 * @param {Array<string>} tokenTypes - Array of token type strings
 * @returns {Promise<Object>} Map of tokenType -> price
 */
export async function fetchMultipleTokenPrices(tokenTypes) {
  if (!tokenTypes || tokenTypes.length === 0) return {};
  
  const priceMap = {};
  
  // Extract unique symbols
  const symbols = new Set();
  const typeToSymbol = {};
  
  tokenTypes.forEach(tokenType => {
    const symbol = extractTokenSymbol(tokenType);
    if (symbol) {
      symbols.add(symbol);
      typeToSymbol[tokenType] = symbol;
    }
  });
  
  if (symbols.size === 0) return {};
  
  // Fetch all prices in one request to our serverless function
  try {
    const symbolsString = Array.from(symbols).join(',');
    console.log(`📡 Fetching ${symbols.size} token prices via serverless...`);
    
    const response = await fetch(`${PRICE_API_ENDPOINT}?symbols=${symbolsString}`);
    
    if (!response.ok) {
      console.log(`❌ Batch price fetch failed: HTTP ${response.status}`);
      return {};
    }
    
    const data = await response.json();
    const prices = data.prices || {};
    
    // Map prices back to token types
    tokenTypes.forEach(tokenType => {
      const symbol = typeToSymbol[tokenType];
      if (symbol && prices[symbol]) {
        const price = prices[symbol];
        priceMap[tokenType] = price;
        
        // Cache it
        priceCache.data[symbol] = price;
        priceCache.lastUpdated[symbol] = Date.now();
      }
    });
    
    console.log(`✅ Fetched ${Object.keys(priceMap).length} prices via serverless`);
    return priceMap;
  } catch (error) {
    console.error('Error fetching batch prices:', error);
    return {};
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CACHE UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Clear price cache
 */
export function clearPriceCache() {
  priceCache.data = {};
  priceCache.lastUpdated = {};
  console.log('🗑️ Price cache cleared');
}

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export function getCacheStats() {
  const cached = Object.keys(priceCache.data).length;
  const oldest = Math.min(...Object.values(priceCache.lastUpdated));
  const newest = Math.max(...Object.values(priceCache.lastUpdated));
  
  return {
    cachedPrices: cached,
    oldestAge: cached > 0 ? Date.now() - oldest : null,
    newestAge: cached > 0 ? Date.now() - newest : null,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT DEFAULT
// ═══════════════════════════════════════════════════════════════════════════

export default {
  fetchTokenPrice,
  calculateUSDValue,
  getValueTier,
  getValueLabel,
  formatUSDValue,
  extractTokenSymbol,
  fetchMultipleTokenPrices,
  clearPriceCache,
  getCacheStats,
};